import React, { useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguagePreference } from "../../hooks/useLanguagePreference";
import { useTranslation } from "react-i18next";
import LanguageSelectionModal from "../modals/LanguageSelectionModal";

interface ProtectedRouteProps {
  requiredRoles?: string[];
  requireVerification?: boolean;
  children?: React.ReactNode;
}

/**
 * Componente para proteger rutas que requieren autenticación
 * @param requiredRoles Roles permitidos para acceder a la ruta
 * @param requireVerification Si requiere que el email esté verificado
 * @param children Componentes hijos (alternativa a Outlet)
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  requiredRoles,
  requireVerification = true,
  children,
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    showLanguageModal,
    handleLanguageSelect,
    handleSkipLanguageSelection,
  } = useLanguagePreference();
  const { i18n } = useTranslation();
  const langPrefix = `/${i18n.language}`;

  // Solo redirigir a verify-email si el usuario está autenticado pero no verificado
  useEffect(() => {
    if (
      user &&
      !user.emailVerified &&
      requireVerification &&
      !location.pathname.includes("/verify-email")
    ) {
      navigate(`${langPrefix}/verify-email`, { replace: true });
    }
  }, [user, requireVerification, location.pathname, navigate, langPrefix]);

  // Mostrar loader mientras se verifica la autenticación
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  // Si no hay usuario, redirigir al login
  if (!user) {
    return <Navigate to={`${langPrefix}/login`} state={{ from: location }} replace />;
  }

  // Si requiere verificación y el usuario no está verificado, redirigir a verify-email
  if (requireVerification && !user.emailVerified) {
    return <Navigate to={`${langPrefix}/verify-email`} replace />;
  }

  // Verificar roles requeridos
  if (requiredRoles && requiredRoles.length > 0) {
    const userRole = user.role;

    // Los administradores tienen acceso a todo
    if (userRole === "admin") {
      return <>{children || <Outlet />}</>;
    }

    // Los especialistas pueden acceder tanto a rutas de teacher como de cliente (dashboard)
    if (userRole === "teacher" || userRole === "especialista") {
      // Permitir acceso si es una ruta de teacher o si está en los roles requeridos
      if (
        requiredRoles.includes("teacher") ||
        requiredRoles.includes("especialista") ||
        requiredRoles.includes("user") ||
        requiredRoles.includes("cliente")
      ) {
        return <>{children || <Outlet />}</>;
      }
      // Solo redirigir si no tiene permisos para esta ruta específica
      return <Navigate to="/specialist" replace />;
    }

    // Si el rol del usuario no está en los roles permitidos, redirigir a la ruta correspondiente
    if (!requiredRoles.includes(userRole)) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Si todo está bien, mostrar el contenido con el modal de idioma si es necesario
  return (
    <>
      {children || <Outlet />}
      <LanguageSelectionModal
        isOpen={showLanguageModal}
        onLanguageSelect={handleLanguageSelect}
        onSkip={handleSkipLanguageSelection}
      />
    </>
  );
};

export default ProtectedRoute;
